import { Icon } from '@iconify/react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { CheckoutResponse, CheckoutSessionResponse } from '../api/checkout';
import { getActiveSession, getCheckout, getSession } from '../api/checkout';
import { isCheckoutSessionTerminalStatus, isCheckoutTerminalStatus } from '../api/checkout';
import OutcomeView from '../components/OutcomeView';
import PaymentView from '../components/PaymentView';
import ProductView from '../components/ProductView';

type PageState = 'loading' | 'product' | 'payment' | 'outcome' | 'error';
type PageErrorCode = 'invalid' | 'not_found' | 'session_missing' | 'generic';

function checkoutExpired(checkout: CheckoutResponse): boolean {
  return new Date(checkout.expiresAt).getTime() <= Date.now();
}

export default function CheckoutPage() {
  const { checkoutId } = useParams<{ checkoutId: string }>();
  const [page, setPage] = useState<PageState>('loading');
  const [checkout, setCheckout] = useState<CheckoutResponse | null>(null);
  const [session, setSession] = useState<CheckoutSessionResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<PageErrorCode>('generic');
  const [linkExpired, setLinkExpired] = useState(false);

  const load = useCallback(async () => {
    if (!checkoutId) {
      setPage('error');
      setErrorCode('invalid');
      setErrorMessage('Invalid link');
      return;
    }
    setPage('loading');
    setErrorMessage(null);
    setErrorCode('generic');
    try {
      const c = await getCheckout(checkoutId);
      setCheckout(c);
      setLinkExpired(false);

      if (checkoutExpired(c)) {
        setSession(null);
        setLinkExpired(true);
        setPage('outcome');
        return;
      }

      if (isCheckoutTerminalStatus(c.status)) {
        // No session here on purpose: `getActiveSession` resolves only
        // AWAITING_PAYMENT / PARTIAL / CONFIRMING / RISK_SCAN_PENDING sessions
        // that have not expired, so a settled one is never returned and the
        // call would 404 every time. `OutcomeView` handles the missing detail
        // by confirming the payment without asserting anything it cannot show.
        setSession(null);
        setPage('outcome');
        return;
      }

      if (c.status === 'OPEN') {
        setSession(null);
        setPage('product');
        return;
      }

      const active = await getActiveSession(checkoutId);
      if (active && !isCheckoutSessionTerminalStatus(active.status)) {
        setSession(active);
        setPage('payment');
        return;
      }

      setSession(null);
      setPage('product');
    } catch {
      setPage('error');
      setErrorCode('not_found');
      setErrorMessage('We could not find this payment link.');
    }
  }, [checkoutId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handlePaymentSession = useCallback(
    async (sessionId: string) => {
      if (!checkoutId) return;
      try {
        const s = await getSession(checkoutId, sessionId);
        setSession(s);
        setPage('payment');
      } catch {
        setErrorCode('session_missing');
        setErrorMessage('No active payment session found. Please start a new payment.');
        setPage('error');
      }
    },
    [checkoutId],
  );

  const handleSessionUpdate = useCallback((s: CheckoutSessionResponse) => {
    setSession(s);
  }, []);

  const handleTerminalFromPayment = useCallback(() => {
    setPage('outcome');
  }, []);

  const handleTimerBackToProduct = useCallback(() => {
    setSession(null);
    setPage('product');
  }, []);

  if (page === 'loading' || !checkoutId) {
    return (
      <div className="ps-page">
        <div className="ps-shell ps-center">
          <div className="ps-subtitle">Loading checkout...</div>
        </div>
      </div>
    );
  }

  if (page === 'error') {
    const icon =
      errorCode === 'not_found' ? 'mdi:file-document-remove-outline'
      : errorCode === 'invalid' ? 'mdi:alert-circle-outline'
      : 'mdi:help-circle-outline';
    const title =
      errorCode === 'not_found' ? 'Checkout not found'
      : errorCode === 'invalid' ? 'Invalid checkout link'
      : errorCode === 'session_missing' ? 'No active session found'
      : 'Something went wrong';
    return (
      <div className="ps-page">
        <div className="ps-shell ps-center">
          <div style={{ width: '100%', maxWidth: 420 }}>
            <div className="ps-icon-circle" style={{ background: '#fef2f2' }}>
              <Icon icon={icon} width={56} height={56} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 22 }}>{title}</div>
            <p className="ps-subtitle">{errorMessage ?? 'Something went wrong.'}</p>
            <div className="ps-alert error">
              {errorMessage ?? 'Something went wrong.'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!checkout) {
    return null;
  }

  if (page === 'outcome') {
    return (
      <OutcomeView
        checkout={checkout}
        session={session}
        checkoutOnly={session == null}
        linkExpired={linkExpired}
      />
    );
  }

  if (page === 'payment' && session) {
    return (
      <PaymentView
        checkout={checkout}
        session={session}
        onSessionUpdate={handleSessionUpdate}
        onTerminal={handleTerminalFromPayment}
        onBack={handleTimerBackToProduct}
      />
    );
  }

  return <ProductView checkout={checkout} onPaymentSession={handlePaymentSession} />;
}
